import Input from "components/Input";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useRef, useState } from "react";
import Button from "components/Button";
import settingsService, {
  IConfigureIntegrationRequest,
} from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import HollowButton from "components/HollowButton";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import { useTranslation } from "react-i18next";
import { readBlobAsBase64 } from "utils/readAsBase64";
import { getErrorMessage } from "utils/getErrorMessage";

// Wczytany certyfikat: nazwa do prezentacji + treść base64 (PFX/P12 — binarne, więc base64).
interface PfxFile {
  name: string;
  content: string;
}

// Atrybuty zniechęcające przeglądarkę/menedżery haseł do auto-uzupełniania pól.
const noAutofillProps = (name: string, autoComplete = "off") => ({
  autoComplete,
  name,
  "data-lpignore": "true",
  "data-form-type": "other",
});

// Konfiguracja skrzynki rządowych e-Doręczeń (Poczta Polska). Użytkownik wnosi
// własny adres ADE + certyfikat klienta (PFX) — BE nim podpisuje JWT do UA API.
const ConfigureEDelivery: FC<Props> = ({ onSuccess, toggle }) => {
  const { t } = useTranslation();

  const { mutate: integration, isLoading } =
    settingsService.useConfigureIntegration();

  const [data, setData] = useState({
    address: "",
    systemName: "",
    password: "",
    syncInterval: "1800",
  });

  const [pfx, setPfx] = useState<PfxFile | null>(null);
  const pfxInput = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const readPfx = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Pozwala ponownie wgrać ten sam plik (reset value inputu).
    event.target.value = "";
    if (!file) return;

    try {
      const content = await readBlobAsBase64(file);
      setError(null);
      setPfx({ name: file.name, content });
    } catch {
      setError("Nie udało się wczytać certyfikatu.");
      setPfx(null);
    }
  };

  const isComplete = !!data.address && !!data.systemName && !!pfx?.content;

  const handleClick = () => {
    const syncInterval = parseInt(data.syncInterval, 10);

    const payload: IConfigureIntegrationRequest = {
      Type: IntegrationType.GovEDelivery,
      Provider: IntegrationProvider.GovEDelivery,
      EDeliveryAddress: data.address.trim(),
      SystemName: data.systemName.trim(),
      Pfx: pfx?.content,
      PfxPassword: data.password || undefined,
      SyncInterval:
        Number.isFinite(syncInterval) && syncInterval > 0
          ? syncInterval
          : undefined,
    };

    setError(null);
    integration(payload, {
      onSuccess: () => {
        onSuccess(data.address);
        toggle();
      },
      onError: (response) => {
        const name = getErrorMessage(response);

        notification({
          type: ENotificationType.ERROR,
          title: t("integrations.createIntegrationFailed"),
          text: name,
        });
        setError(name);
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>Konfiguracja e-Doręczeń (rządowych)</div>

      <div className={styles.body}>
        Podłącz własną skrzynkę e-Doręczeń (Poczta Polska). Podaj adres do
        doręczeń elektronicznych (ADE), nazwę systemu oraz certyfikat klienta
        (PFX) wraz z hasłem — certyfikatem podpisywana jest autoryzacja do
        systemu e-Doręczeń.
      </div>

      <div className={styles.form}>
        <Input
          value={data.address}
          onChange={(v) => setData({ ...data, address: v })}
          label="Adres do doręczeń elektronicznych (ADE)"
          autoComplete="off"
          inputProps={noAutofillProps("gov-ade-field")}
        />
        <Input
          value={data.systemName}
          onChange={(v) => setData({ ...data, systemName: v })}
          label="Nazwa systemu"
          autoComplete="off"
          inputProps={noAutofillProps("gov-system-field")}
        />
        <Input
          value={data.password}
          type="password"
          onChange={(v) => setData({ ...data, password: v })}
          label="Hasło do certyfikatu"
          autoComplete="new-password"
          inputProps={noAutofillProps("gov-pfxpass-field", "new-password")}
        />
        <Input
          value={data.syncInterval}
          onChange={(v) =>
            setData({ ...data, syncInterval: v.replace(/[^0-9]/g, "") })
          }
          label="Częstotliwość synchronizacji (s)"
          autoComplete="off"
        />

        <div className={styles.fileRow}>
          <span className={styles.fileLabel}>Certyfikat (PFX)</span>
          {pfx ? (
            <div className={styles.fileChip}>
              <span className={styles.fileName} title={pfx.name}>
                {pfx.name}
              </span>
              <button
                type="button"
                className={styles.fileRemove}
                onClick={() => setPfx(null)}
                title={t("common.delete")}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={styles.fileButton}
              onClick={() => pfxInput.current?.click()}
            >
              {t("integrations.kSeFUploadFile")}
            </button>
          )}
          <input
            type="file"
            accept=".pfx,.p12"
            ref={pfxInput}
            style={{ display: "none" }}
            onChange={readPfx}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.buttonsWrapper}>
          <HollowButton text={t("common.cancel")} onClick={() => toggle()} />
          <Button
            text={t("common.save")}
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading || !isComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfigureEDelivery;
